import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

const PILL_SPRING = { type: "spring" as const, stiffness: 420, damping: 34 };
const UNDERLINE_SPRING = { type: "spring" as const, stiffness: 380, damping: 30 };

const dropdownListVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};

const dropdownItemVariants = {
  hidden: { opacity: 0, x: -8 },
  show: { opacity: 1, x: 0 },
};

function LanguageToggle({ className = "" }: { className?: string }) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === "en";

  return (
    <button
      type="button"
      onClick={() => i18n.changeLanguage(isEnglish ? "es" : "en")}
      aria-label={isEnglish ? "Cambiar a español" : "Switch to English"}
      className={`rounded-xl px-2.5 py-2 text-xs font-semibold text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-primary)] ${className}`}
    >
      {isEnglish ? "ES" : "EN"}
    </button>
  );
}

function NavLinkItem({
  to,
  label,
  isActive,
  hoveredKey,
  onHover,
}: {
  to: string;
  label: string;
  isActive: boolean;
  hoveredKey: string | null;
  onHover: (key: string) => void;
}) {
  return (
    <li className="relative" onMouseEnter={() => onHover(to)}>
      {hoveredKey === to && (
        <motion.span
          layoutId="nav-hover-pill"
          className="absolute inset-0 rounded-lg bg-[var(--color-surface-hover)]"
          transition={PILL_SPRING}
        />
      )}
      <Link
        to={to}
        className={`relative z-10 block rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${
          isActive
            ? "text-[var(--color-text-primary)]"
            : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
        }`}
      >
        {label}
        {isActive && (
          <motion.span
            layoutId="nav-active-underline"
            className="absolute bottom-1 left-3.5 right-3.5 h-[1.5px] rounded-full bg-[var(--color-accent)]"
            transition={UNDERLINE_SPRING}
          />
        )}
      </Link>
    </li>
  );
}

function SolutionsDropdown({
  isActive,
  hoveredKey,
  onHover,
}: {
  isActive: boolean;
  hoveredKey: string | null;
  onHover: (key: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const solutionsLinks = [
    {
      to: "/soluciones/sitios-web",
      label: t("nav.solutionsItems.websites.label"),
      desc: t("nav.solutionsItems.websites.desc"),
    },
    {
      to: "/soluciones/software-a-medida",
      label: t("nav.solutionsItems.software.label"),
      desc: t("nav.solutionsItems.software.desc"),
    },
    {
      to: "/soluciones/automatizacion-datos",
      label: t("nav.solutionsItems.automation.label"),
      desc: t("nav.solutionsItems.automation.desc"),
    },
  ];

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative" onMouseEnter={() => onHover("soluciones")}>
      {hoveredKey === "soluciones" && (
        <motion.span
          layoutId="nav-hover-pill"
          className="absolute inset-0 rounded-lg bg-[var(--color-surface-hover)]"
          transition={PILL_SPRING}
        />
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className={`relative z-10 flex items-center gap-1 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${
          isActive || open
            ? "text-[var(--color-text-primary)]"
            : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
        }`}
      >
        {t("nav.solutions")}
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          className="flex"
        >
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        </motion.span>
        {isActive && (
          <motion.span
            layoutId="nav-active-underline"
            className="absolute bottom-1 left-3.5 right-3.5 h-[1.5px] rounded-full bg-[var(--color-accent)]"
            transition={UNDERLINE_SPRING}
          />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 400, damping: 32 }}
            className="absolute left-1/2 top-[calc(100%+0.75rem)] w-[340px] -translate-x-1/2 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-primary)]/97 shadow-[0_20px_60px_rgba(0,0,0,0.5)] backdrop-blur-xl"
          >
            <motion.ul
              className="p-2"
              initial="hidden"
              animate="show"
              variants={dropdownListVariants}
            >
              {solutionsLinks.map(({ to, label, desc }) => (
                <motion.li key={to} variants={dropdownItemVariants}>
                  <Link
                    to={to}
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className="block rounded-xl px-4 py-3 transition-colors hover:bg-[var(--color-surface-hover)]"
                  >
                    <p className="text-sm font-semibold text-[var(--color-text-primary)]">{label}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-[var(--color-text-secondary)]">
                      {desc}
                    </p>
                  </Link>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileSolutionsAccordion({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isActive = location.pathname.startsWith("/soluciones");

  const solutionsLinks = [
    { to: "/soluciones/sitios-web", label: t("nav.solutionsItems.websites.label") },
    { to: "/soluciones/software-a-medida", label: t("nav.solutionsItems.software.label") },
    { to: "/soluciones/automatizacion-datos", label: t("nav.solutionsItems.automation.label") },
  ];

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
          isActive
            ? "text-[var(--color-accent)] bg-[var(--color-accent)]/8"
            : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)]"
        }`}
      >
        {t("nav.solutions")}
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          className="flex"
        >
          <ChevronDown className="h-4 w-4" aria-hidden />
        </motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="ml-2 mt-1 space-y-1 overflow-hidden border-l border-[var(--color-border)] pl-3"
          >
            {solutionsLinks.map(({ to, label }) => (
              <li key={to}>
                <Link
                  to={to}
                  onClick={onNavigate}
                  className="block rounded-lg px-3 py-2.5 text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
                >
                  {label}
                </Link>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
}

export default function Navbar() {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const location = useLocation();

  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/nosotros", label: t("nav.about") },
  ];

  const afterSolutionsLinks = [
    { to: "/portafolio", label: t("nav.portfolio") },
    { to: "/agendar", label: t("nav.schedule") },
    { to: "/soporte", label: t("nav.support") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-4 pt-4 sm:px-6 sm:pt-5">
      <nav
        className={`mx-auto flex max-w-5xl items-center justify-between gap-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-primary)]/88 px-5 py-3 backdrop-blur-xl transition-all duration-300 sm:px-7 ${
          scrolled
            ? "shadow-[0_12px_48px_rgba(0,0,0,0.6)]"
            : "shadow-[0_4px_20px_rgba(0,0,0,0.3)]"
        }`}
      >
        <Link
          to="/"
          className="shrink-0 text-base font-bold tracking-tight text-[var(--color-text-primary)] transition-opacity hover:opacity-70"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          taCode
        </Link>

        <ul
          className="hidden items-center gap-0.5 md:flex"
          onMouseLeave={() => setHoveredKey(null)}
        >
          {navLinks.map(({ to, label }) => (
            <NavLinkItem
              key={to}
              to={to}
              label={label}
              isActive={isActive(to)}
              hoveredKey={hoveredKey}
              onHover={setHoveredKey}
            />
          ))}
          <li>
            <SolutionsDropdown
              isActive={location.pathname.startsWith("/soluciones")}
              hoveredKey={hoveredKey}
              onHover={setHoveredKey}
            />
          </li>
          {afterSolutionsLinks.map(({ to, label }) => (
            <NavLinkItem
              key={to}
              to={to}
              label={label}
              isActive={isActive(to)}
              hoveredKey={hoveredKey}
              onHover={setHoveredKey}
            />
          ))}
        </ul>

        <div className="hidden items-center gap-1 md:flex">
          <LanguageToggle />
          <Link
            to="/cotizar"
            className="group ml-1 flex items-center gap-1.5 rounded-xl bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-black transition-all duration-200 hover:bg-[var(--color-accent-hover)] hover:shadow-[0_0_20px_rgba(16,185,129,0.35)] active:scale-95"
          >
            {t("nav.quote")}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <div className="flex items-center gap-1 md:hidden">
          <LanguageToggle />
          <button
            type="button"
            onClick={() => setIsMenuOpen((v) => !v)}
            className="relative rounded-xl p-2 text-[var(--color-text-secondary)] transition-transform hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-primary)] active:scale-90"
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={isMenuOpen ? "x" : "menu"}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="block"
              >
                {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 34 }}
            className="mx-auto mt-2 max-w-5xl overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-primary)]/95 backdrop-blur-xl md:hidden"
          >
            <div className="px-2 py-2">
              <ul>
                {navLinks.map(({ to, label }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                        isActive(to)
                          ? "text-[var(--color-accent)] bg-[var(--color-accent)]/8"
                          : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)]"
                      }`}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
                <MobileSolutionsAccordion onNavigate={() => setIsMenuOpen(false)} />
                {afterSolutionsLinks.map(({ to, label }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                        isActive(to)
                          ? "text-[var(--color-accent)] bg-[var(--color-accent)]/8"
                          : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)]"
                      }`}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-2 border-t border-[var(--color-border)] p-3">
                <Link
                  to="/cotizar"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--color-accent)] px-4 py-3 text-sm font-semibold text-black transition-all active:scale-95 hover:bg-[var(--color-accent-hover)]"
                >
                  {t("nav.requestQuote")}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
